/**
 * ── THE CHOROPLETH: a map card at `country` grouping (VERA-260) ─────────────────────
 *
 * The pin renderer next door (`ScansByLocation`) plots one mark per located EVENT. This
 * one fills one polygon per COUNTRY. They are the two renders of the map card's grouping
 * axis (`AnalyticsMapGrouping`), and which one a card shows is decided by its id's
 * `@country` suffix — see `analyticsDisplayVariants`, which picks between them.
 *
 * ⚠️ IT USED TO BE ITS OWN CARD (`scans_by_country`) WITH ITS OWN METRIC PICKER, and both
 * are gone. The card existed because grouping was not yet an axis, so the choropleth had
 * nowhere to live but a card of its own — a build-order artefact `AnalyticsMapGrouping`'s
 * docblock names as such. The picker existed for the same reason: three measures that
 * could not be reached any other way, bundled behind an in-card toggle whose choice was
 * never persisted (a `useState`, so every reload reopened on `scans`).
 *
 * Both are now expressed by the id. The picker's three columns map onto three
 * `COUNTRY_GROUPABLE` entries EXACTLY — `scans`, `invalid_scan_rate`, `repeat_scan_rate` —
 * so retiring it decomposes the card into three addressable ones rather than losing a
 * capability, and it gains a fourth the picker never offered (`invalid_scans` as a
 * per-country COUNT, which the endpoint has returned since the day it shipped). The old id
 * forward-maps in `migrateAnalyticsCardId`, so a saved view holding it still opens.
 *
 * A separate COMPONENT from `ScansByLocation` — this is the part that was always right and
 * stays. It consumes a different endpoint (`/analytics/admin/countries`, pre-aggregated)
 * rather than that card's scan points, so one component holding both would mean two
 * unrelated data sources with a mode switch deciding which is live. And its unit of
 * interaction is a COUNTRY, not a scan — no selection, no docked detail panel, no scan
 * list — so almost none of that card's ~2,000 lines would apply.
 *
 * ## ⚠️ THE SCAN-STATUS FILTER AND THE INVALID RATE INTERACT, and the card says so
 *
 * The page-global scan-status filter is merged into the request by
 * `useAnalyticsCountryMetrics` (as it is for every card that reads the `scans` table).
 * Filtering the page to "Invalid" therefore makes every country's invalid rate exactly
 * 100% — the numerator and denominator become the same population. The map stays
 * arithmetically correct and becomes completely uninformative.
 *
 * That is surfaced as a note rather than prevented. Suppressing the filter for this one
 * metric would make the card disagree with every figure beside it, which is a worse
 * failure than a self-evident 100% with a line explaining it — and the reader is entitled
 * to see what the filter they set actually did.
 *
 * ## What the footnote is for
 *
 * `unresolved` and `unattributed_scans` are the two ways a scan can fail to reach this
 * map, and the card prints them. An unpainted country is indistinguishable from a country
 * with no scans, so a map that silently dropped 4% of its scans would look complete and
 * be wrong. See `ICountryMetrics` — the endpoint returns the offending country strings BY
 * VALUE precisely so this line can be specific.
 */
import React, { useMemo } from 'react'

import { Box, Stack, useTheme } from '@mui/material'

import type { ICountryMetricRow, ICountryMetrics } from '@repo/types'
import { Map, type MapChoroplethConfig, type MapChoroplethDatum } from '@repo/ui'

import { type CountryGroupingValue } from 'components/analytics'
import { GOOGLE_MAPS_API_KEY } from 'config'
import { useAnalyticsCountryMetrics } from 'hooks/api'
import type { AnalyticsFilters } from 'store/analytics/types'

/**
 * ⚠️ THE FRACTION IS BUILT FROM `COUNTRY_GROUPABLE`'S DESCRIPTOR, not from a table here.
 *
 * This card used to own a `METRICS: Record<CountryMetricId, MetricSpec>` — three hardcoded
 * accessors reaching into `ICountryMetricRow`. `COUNTRY_GROUPABLE` now carries the same
 * information as an EXECUTABLE `keyof ICountryMetricRow` descriptor, authored beside the
 * capability answer that decides whether the metric may be drawn this way at all, and
 * that is the better home for exactly the reason its own docblock gives: a renamed or
 * dropped column becomes a compile error at the capability record rather than an unpainted
 * map here.
 *
 * Keeping both would have been two expressions of one rule, and a capability record that
 * says a metric is groupable while this file has no accessor for it is precisely how a
 * "yes" comes to promise something no code issues.
 *
 * The one asymmetry preserved from the old table: a COUNT states itself as both halves of
 * the fraction. That uniform shape is what lets the tooltip and the small-sample rule work
 * with no special case — and the rule correctly never fires for a count, because 2 scans is
 * not an unreliable estimate of anything. It is exactly 2 scans.
 */
const readFraction = (
  row: ICountryMetricRow,
  value: CountryGroupingValue,
): { value: number; numerator: number; denominator: number } | null => {
  const numerator = row[value.numerator]
  if (value.kind === 'count') {
    return { value: numerator, numerator, denominator: numerator }
  }
  const denominator = row[value.denominator]
  // A row whose denominator is zero cannot support this metric. `null` rather than 0 —
  // "no items were scanned here" and "nothing scanned here repeated" are different facts,
  // and the second implies data the first does not have.
  if (denominator <= 0) return null
  return { value: numerator / denominator, numerator, denominator }
}

/**
 * The gap line under the legend.
 *
 * Built from the endpoint's own two gap figures and NAMES the offending country strings
 * when there are few enough to name. A count alone ("312 scans could not be matched")
 * tells a reader something is wrong and gives them nothing to do about it; the value is
 * what turns it into a five-minute alias fix (see `country-codes.ts`).
 *
 * Returns undefined when there is no gap, so the legend renders nothing rather than a
 * reassuring "0 scans unaccounted for" — a line that is always present stops being read.
 */
export const buildCountryFootnote = (
  data: ICountryMetrics | undefined,
): string | undefined => {
  if (!data) return undefined
  const parts: string[] = []

  if (data.unresolved.scans > 0) {
    const named = data.unresolved.values
      .slice(0, 3)
      .map(v => `"${v.value}"`)
      .join(', ')
    const more = data.unresolved.values.length > 3 ? ', …' : ''
    parts.push(
      `${data.unresolved.scans.toLocaleString()} ${
        data.unresolved.scans === 1 ? 'scan' : 'scans'
      } could not be matched to a country on this map (${named}${more}).`,
    )
  }

  if (data.unattributed_scans > 0) {
    parts.push(
      `${data.unattributed_scans.toLocaleString()} ${
        data.unattributed_scans === 1 ? 'scan has' : 'scans have'
      } no country recorded.`,
    )
  }

  return parts.length ? parts.join(' ') : undefined
}

export interface ScansByCountryProps {
  /**
   * The page's whole filter state, not six unpacked props.
   *
   * Matches `TopInfluencers` exactly, and the reason is not brevity: `dateRange.from/to`
   * are `Date`, and every consumer of this data needs them as ISO strings. Converting
   * once inside the card means one `.toISOString()` in the codebase rather than one per
   * call site — and a card that took `from: string` would push that conversion onto
   * whoever renders it, which is how a second call site ends up passing a `Date` and
   * serialising it some other way.
   */
  filters: AnalyticsFilters
  /**
   * Map height. A number is px; `'100%'` fills the card's own box, which is what an
   * EXPANDED (maximised) card wants — the same shape `ScansByLocation` uses, where the
   * height comes from the region rather than from a constant.
   */
  height?: number | string
  /**
   * WHICH measure fills a country, and what to call it — the card's identity, handed in
   * rather than chosen inside.
   *
   * REQUIRED, and there is no local fallback. An optional descriptor would need a default,
   * and the only honest default is "scans" — which is how the old in-card picker came to
   * silently reopen every saved view on a metric the reader had not chosen. A card whose
   * id says `mkt_repeat_scan_rate::map@country` must paint the repeat rate or paint
   * nothing; there is no third answer worth having a prop shape for.
   */
  value: CountryGroupingValue
  /** What the COLOUR means, for the legend. See `analyticsMapMetricLabel` for why this is
   *  the metric's own label and not the card's title. */
  label: string
  /**
   * Appended to the endpoint's own gap line.
   *
   * The pins-vs-country caveat goes here — at the number it explains — but only when the
   * card can ALSO be grouped by pins, because on a country-only metric (a rate) there is
   * no pin count for a reader to compare against and the sentence would name a comparison
   * they cannot make. The page decides that; this card just prints what it is given.
   */
  footnoteSuffix?: string
}

const PANEL_HEIGHT = 600

export const ScansByCountry: React.FC<ScansByCountryProps> = ({
  filters,
  height = PANEL_HEIGHT,
  value,
  label,
  footnoteSuffix,
}) => {
  const theme = useTheme()
  const mapStyle = theme.palette.mode === 'dark' ? 'dark' : 'light'

  const { data, isLoading } = useAnalyticsCountryMetrics({
    from: filters.dateRange.from.toISOString(),
    to: filters.dateRange.to.toISOString(),
    owner_ids: filters.ownerIds,
    product_ids: filters.productIds,
    certificate_ids: filters.certificateIds,
    certificate_statuses: filters.certificateStatuses,
    location: filters.location,
  })

  const choropleth: MapChoroplethConfig | undefined = useMemo(() => {
    if (!data) return undefined

    const rows: MapChoroplethDatum[] = []
    data.rows.forEach(row => {
      const fraction = readFraction(row, value)
      if (!fraction) return
      rows.push({ code: row.code, name: row.name, ...fraction })
    })

    /*
      The endpoint's own gap line, then the grouping caveat. Both explain a reason the map
      does not say what a reader might assume — one about scans that are MISSING from it,
      one about scans that are on it and not on the pin map — so they belong in the same
      place, in that order (what is absent before what is over-counted).
    */
    const gap = buildCountryFootnote(data)
    const footnote = [gap, footnoteSuffix].filter(Boolean).join(' ') || undefined

    return {
      data: rows,
      kind: value.kind,
      label,
      /*
        Applied to RATES only, by the map (see `MapChoroplethConfig`). The threshold comes
        from the SERVER's echoed value rather than a constant here, so the client cannot
        colour by one rule while the server reasoned with another.
      */
      minDenominator: value.kind === 'rate' ? data.min_denominator : undefined,
      /*
        The map cannot know what this metric divides by, so it is named here — from the
        DESCRIPTOR's own noun, never a constant "scans". The repeat rate divides by
        CERTIFICATES, and the built-in wording once told a reader the threshold was 30
        scans while it was suppressing every country with fewer than 30 items scanned
        there: a much higher bar, and on a three-day window it suppressed all fifteen
        countries while naming a criterion none of them had failed. Caught by looking at
        it; no typechecker sees a sentence that is wrong.
      */
      insufficientLabel:
        value.kind === 'rate'
          ? `Fewer than ${data.min_denominator.toLocaleString()} ${value.denominatorNoun}`
          : undefined,
      footnote,
    }
  }, [data, value, label, footnoteSuffix])

  return (
    <Stack spacing={3} sx={{ height: '100%' }}>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Map
          items={[]}
          apiKey={GOOGLE_MAPS_API_KEY || ''}
          mapStyle={mapStyle}
          height={height}
          // 2 shows the whole world, which is the only framing a country choropleth has.
          // There is deliberately no fit-to-data: zooming to the countries that happen to
          // have scans hides the ones that do not, and "nowhere in South America" is a
          // finding this map exists to show.
          defaultZoom={2}
          renderMode="choropleth"
          choropleth={choropleth}
          isLoading={isLoading && !data}
          showLoadingState
          showErrorState
          errorMessage="Google Maps API key not configured"
          emptyMessage="No scans with a recorded country in this range"
          gestureHandling="greedy"
        />
      </Box>
    </Stack>
  )
}
